// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@saas-boilerplate/shared';
import { useAuth } from '@/contexts/auth-context';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
// Card components no longer used - auth page uses a clean borderless layout
import { Alert, AlertDescription } from './ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Separator } from './ui/separator';
import { Loader2, AlertCircle, Calendar, ListChecks, Sparkles } from 'lucide-react';
import { Badge } from './ui/badge';
import { getLastUsedLoginType } from '@/lib/last-used-login-type';
import { PasswordReset } from './password-reset';
import appLogo from '@/assets/upstand_ai_logo.svg';

interface AuthProps {
  onAuthenticated: () => void;
}

/** Right-hand marketing column (login & password reset) - warm shell aligned with app chrome. */
function AuthMarketingPanel() {
  const points = [
    {
      icon: Calendar,
      title: 'Your meetings, in context',
      body:
        'Connect your calendar so every session shows up with the right prep: less scrambling, more presence.',
    },
    {
      icon: ListChecks,
      title: 'Action items that survive the call',
      body:
        'Turn discussion into clear owners and next steps, so nothing important fades when the meeting ends.',
    },
    {
      icon: Sparkles,
      title: 'More productive meetings',
      body:
        'A focused workspace for teams who want follow-through and fewer dropped threads between tools.',
    },
  ];

  return (
    <div className="hidden lg:flex relative min-h-0 flex-col justify-center overflow-hidden border-l border-border/70 bg-muted/40 px-10 xl:px-14 py-12">
      <div
        className="absolute inset-0 bg-gradient-to-br from-background via-primary/5 to-primary-container/25"
        aria-hidden
      />
      <div
        className="absolute inset-0 opacity-90 bg-[radial-gradient(ellipse_85%_60%_at_85%_15%,rgb(233_179_46/0.35),transparent_55%),radial-gradient(ellipse_70%_55%_at_10%_90%,rgb(250_169_101/0.28),transparent_50%)]"
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-t from-secondary-container/40 to-transparent pointer-events-none" aria-hidden />
      <div className="relative z-10 max-w-lg">
        <h2 className="font-display text-3xl xl:text-[2rem] font-semibold leading-tight tracking-tight text-headline">
          Calmer meetings, clearer follow-through
        </h2>
        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
          Upstand.ai helps your team capture what matters from every conversation, so decisions, context, and next steps
          stay in one warm, focused place.
        </p>
        <ul className="mt-10 space-y-8">
          {points.map(({ icon: Icon, title, body }) => (
            <li key={title} className="flex gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/12 ring-1 ring-primary/20">
                <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
              </div>
              <div>
                <p className="font-semibold text-headline">{title}</p>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{body}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function Auth({ onAuthenticated }: AuthProps) {
  const { signIn, signUp, isLoading: authLoading, error: authError, authState } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  const getAppUrl = () => {
    const configured = import.meta.env.VITE_APP_URL?.trim();
    if (configured) return configured.replace(/\/$/, '');
    return window.location.origin;
  };

  // Sign Up Form State
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupFullName, setSignupFullName] = useState('');

  // Sign In Form State
  const [signinEmail, setSigninEmail] = useState('');
  const [signinPassword, setSigninPassword] = useState('');
  const [lastUsedLoginType, setLastUsedLoginTypeState] = useState(() => getLastUsedLoginType());

  // PKCE saves provider after session resolves; refresh hint when landing signed-out (e.g. after sign-out).
  useEffect(() => {
    if (authState === 'unauthenticated') {
      setLastUsedLoginTypeState(getLastUsedLoginType());
    }
  }, [authState]);

  // Use auth context error if available
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  // Debug Supabase configuration on mount
  useEffect(() => {
    console.log('🔧 Supabase Client Configuration:');
    console.log('  - Project URL:', supabase.supabaseUrl);
    console.log('  - Current Origin:', window.location.origin);
    console.log('  - Environment:', window.location.hostname);
  }, []);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { error } = await signUp(signupEmail, signupPassword, signupFullName);

      if (error) {
        setError(error.message || 'An error occurred during sign up');
        return;
      }

      setSuccess('Account created successfully! Please check your email to confirm your account before signing in.');
        setSignupEmail('');
        setSignupPassword('');
        setSignupFullName('');
    } catch (error: any) {
      setError(error.message || 'An error occurred during sign up');
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { error } = await signIn(signinEmail, signinPassword);

      if (error) {
        setError(error.message || 'An error occurred during sign in');
        return;
      }

      // Auth context will handle the state change, which will trigger routing
      // The onAuthenticated callback will be handled by the auth state machine
      onAuthenticated();
    } catch (error: any) {
      setError(error.message || 'An error occurred during sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider: 'google' | 'azure') => {
    setLoading(true);
    setError(null);

    try {
      // Use configured app URL in deployed environments; fallback to current origin for local dev.
      const currentOrigin = getAppUrl();
      
      console.log('🔐 Starting OAuth Sign In:', { 
        provider, 
        redirectTo: currentOrigin,
        hostname: window.location.hostname,
        pathname: window.location.pathname
      });

      const scopes =
        provider === 'google'
          ? 'openid email profile https://www.googleapis.com/auth/calendar.events'
          : 'openid profile email offline_access User.Read Calendars.ReadWrite';

      const queryParams =
        provider === 'google'
          ? { access_type: 'offline', prompt: 'consent' }
          // Azure AD only accepts a SINGLE prompt value (login|none|consent|select_account);
          // space-combining them (as Google allows) throws AADSTS90023. offline_access in the
          // scope is what yields the refresh token, so select_account alone is sufficient.
          : { prompt: 'select_account' };

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: currentOrigin,
          skipBrowserRedirect: false,
          scopes,
          queryParams,
        },
      });

      if (error) {
        console.error('❌ OAuth initiation error:', error);
        throw error;
      }
      
      console.log('✅ OAuth initiation successful, redirecting to provider...', data);
      // Browser will redirect to OAuth provider now
      // When it comes back, App.tsx will handle the callback
    } catch (error: any) {
      console.error('❌ OAuth exception:', error);
      setError(error.message || `An error occurred during ${provider} sign in`);
      setLoading(false);
    }
  };

  // Password reset is rendered inline (not as an overlay) for a smooth transition
  if (showPasswordReset) {
    return (
      <div className="h-screen grid lg:grid-cols-2 overflow-hidden">
        {/* Left side - reset form */}
        <div className="flex items-center justify-center bg-background p-6 sm:p-8 overflow-y-auto">
          <div className="w-full max-w-md rounded-2xl border border-border/80 bg-card p-8 shadow-lg shadow-black/5">
            <div className="flex justify-center mb-8">
              <img src={appLogo} alt="Upstand.ai" width={280} height={66} className="w-full max-w-[280px] h-auto object-contain object-left" />
            </div>

            <h1 className="text-2xl font-semibold text-headline text-center mb-2">Reset your password</h1>
            <p className="text-sm text-muted-foreground text-center mb-8">
              We&apos;ll email you a link to choose a new password.
            </p>

            <PasswordReset
              onSuccess={() => {
                setShowPasswordReset(false);
                setSuccess('Password reset email sent! Check your inbox.');
              }}
              onCancel={() => setShowPasswordReset(false)}
            />
          </div>
        </div>

        <AuthMarketingPanel />
      </div>
    );
  }

  return (
    <div className="h-screen grid lg:grid-cols-2 overflow-hidden">
      {/* Left side - auth form */}
      <div className="flex items-center justify-center bg-background p-6 sm:p-8 overflow-y-auto">
        <div className="w-full max-w-md rounded-2xl border border-border/80 bg-card p-8 shadow-lg shadow-black/5">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img src={appLogo} alt="Upstand.ai" width={280} height={66} className="w-full max-w-[280px] h-auto object-contain object-left" />
          </div>

          <p className="text-sm text-muted-foreground text-center mb-8">
            Welcome back - sign in to open your workspace.
          </p>

          {/* OAuth buttons */}
          <div className="space-y-3 mb-6">
            <div className="relative">
              <Button
                type="button"
                variant="outline"
                className="w-full h-11 text-sm font-normal bg-white"
                onClick={() => handleOAuthSignIn('google')}
                disabled={loading}
              >
                <svg className="mr-2 h-4 w-4 shrink-0" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </Button>
              {lastUsedLoginType === 'google' ? (
                <Badge variant="outline" className="pointer-events-none absolute -top-2.5 right-1 border-[#e0d2ad] bg-[#efe1c1] text-[10px] font-medium tracking-wide text-[#6b5d4a]">
                  Last used
                </Badge>
              ) : null}
            </div>
            <div className="relative">
              <Button
                type="button"
                variant="outline"
                className="w-full h-11 text-sm font-normal bg-white"
                onClick={() => handleOAuthSignIn('azure')}
                disabled={loading}
              >
                <svg className="mr-2 h-4 w-4 shrink-0" viewBox="0 0 23 23">
                  <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
                  <path fill="#f35325" d="M1 1h10v10H1z"/>
                  <path fill="#81bc06" d="M12 1h10v10H12z"/>
                  <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                  <path fill="#ffba08" d="M12 12h10v10H12z"/>
                </svg>
                Continue with Microsoft
              </Button>
              {lastUsedLoginType === 'azure' ? (
                <Badge variant="outline" className="pointer-events-none absolute -top-2.5 right-1 border-[#e0d2ad] bg-[#efe1c1] text-[10px] font-medium tracking-wide text-[#6b5d4a]">
                  Last used
                </Badge>
              ) : null}
            </div>
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <Separator />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs text-muted-foreground">
              or
            </span>
          </div>

          {/* Tabs for Sign In / Sign Up */}
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>

            {/* Sign In */}
            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="signin-email" className="text-sm">Email</Label>
                  <Input
                    id="signin-email"
                    type="email"
                    placeholder="you@example.com"
                    value={signinEmail}
                    onChange={(e) => setSigninEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="h-11 bg-white"
                    autoFocus
                  />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="signin-password" className="text-sm">Password</Label>
                    <button
                      type="button"
                      onClick={() => setShowPasswordReset(true)}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                      disabled={loading}
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input
                    id="signin-password"
                    type="password"
                    placeholder="••••••••"
                    value={signinPassword}
                    onChange={(e) => setSigninPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="h-11 bg-white"
                  />
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {success && (
                  <Alert className="bg-green-50 border-green-200">
                    <AlertDescription className="text-green-800">{success}</AlertDescription>
                  </Alert>
                )}

                <div className="relative">
                  <Button
                    type="submit"
                    className="w-full h-11"
                    disabled={loading || authLoading}
                  >
                    {(loading || authLoading) ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                  {!loading && !authLoading && lastUsedLoginType === 'email' ? (
                    <Badge variant="outline" className="pointer-events-none absolute -top-2.5 right-1 border-[#e0d2ad] bg-[#efe1c1] text-[10px] font-medium tracking-wide text-[#6b5d4a]">
                      Last used
                    </Badge>
                  ) : null}
                </div>
              </form>
            </TabsContent>

            {/* Sign Up */}
            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="signup-name" className="text-sm">Full Name</Label>
                  <Input
                    id="signup-name"
                    type="text"
                    placeholder="John Doe"
                    value={signupFullName}
                    onChange={(e) => setSignupFullName(e.target.value)}
                    required
                    disabled={loading}
                    className="h-11 bg-white"
                    autoFocus
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signup-email" className="text-sm">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="you@example.com"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="h-11 bg-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signup-password" className="text-sm">Password</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="••••••••"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="h-11 bg-white"
                    minLength={6}
                  />
                  <p className="text-xs text-muted-foreground">Must be at least 6 characters</p>
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {success && (
                  <Alert className="bg-green-50 border-green-200">
                    <AlertDescription className="text-green-800">{success}</AlertDescription>
                  </Alert>
                )}

                <Button type="submit" className="w-full h-11" disabled={loading || authLoading}>
                  {(loading || authLoading) ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    'Create your account'
                  )}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  No credit card required to get started.
                </p>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-8 pt-6 border-t border-border/50">
            <p className="text-xs text-muted-foreground text-center">
              By continuing, you agree to our{' '}
              <span className="underline underline-offset-2">Terms of Service</span>
              {' '}and{' '}
              <span className="underline underline-offset-2">Privacy Policy</span>
              .
            </p>
          </div>
        </div>
      </div>

      <AuthMarketingPanel />
    </div>
  );
}